import { resolvePublicImageUrl } from '../helper/helper';
import { Api } from '../services/interceptor';
import { QUERY_KEY } from '../services/queryEndPoint';

export type GalleryApiRow = Record<string, unknown>;

export type GalleryCardModel = {
  match: string;
  date: string;
  photoCount: number;
  matchId?: string;
  imageUrl: string;
  images: string[];
  /** Raw path/URL from API (before resolvePublicImageUrl); preferred for delete payload imageIdList. */
  primaryImageUrlRaw?: string;
};

function pickRawImageString(entry: unknown): string | undefined {
  if (entry == null) return undefined;
  if (typeof entry === 'string') {
    const t = entry.trim();
    return t || undefined;
  }
  if (typeof entry === 'object') {
    const o = entry as Record<string, unknown>;
    for (const key of ['url', 'imageUrl', 'imageUri', 'uri', 'path'] as const) {
      const v = o[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return undefined;
}

function unwrapGalleryList(raw: unknown): GalleryApiRow[] {
  if (Array.isArray(raw)) return raw as GalleryApiRow[];
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const key of [
      'imageList',
      'galleryList',
      'data',
      'content',
      'items',
      'records',
    ] as const) {
      const v = o[key];
      if (Array.isArray(v)) return v as GalleryApiRow[];
    }
    // e.g. { data: { imageList: [...] } }
    const nested = o.data;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const n = nested as Record<string, unknown>;
      for (const key of ['imageList', 'galleryList'] as const) {
        const v = n[key];
        if (Array.isArray(v)) return v as GalleryApiRow[];
      }
    }
  }
  return [];
}

export function mapGalleryApiToCards(
  raw: unknown,
  placeholderSrc: string,
): GalleryCardModel[] {
  return unwrapGalleryList(raw).map((item) => {
    const imagesRaw = item.images;
    const rawStrings: string[] = [];
    if (Array.isArray(imagesRaw)) {
      for (const e of imagesRaw) {
        const s = pickRawImageString(e);
        if (s) rawStrings.push(s);
      }
    }
    const primaryRaw =
      pickRawImageString(item.imageUrl) ??
      pickRawImageString(item.imageUri) ??
      rawStrings[0];

    const resolved = rawStrings
      .map((u) => resolvePublicImageUrl(u))
      .filter((u) => u.length > 0);
    const resolvedPrimary = primaryRaw ? resolvePublicImageUrl(primaryRaw) : '';

    const images =
      resolved.length > 0
        ? resolved
        : resolvedPrimary
          ? [resolvedPrimary]
          : [];

    const n = Number(item.photoCount);
    const photoCount =
      rawStrings.length ||
      (Number.isFinite(n) && n > 0 ? n : 0) ||
      (primaryRaw ? 1 : 0) ||
      0;

    return {
      match: String(item.match ?? item.matchName ?? 'Gallery'),
      date: String(item.date ?? item.matchDate ?? ''),
      photoCount,
      matchId:
        item.matchId != null
          ? String(item.matchId)
          : item.id != null
            ? String(item.id)
            : undefined,
      imageUrl: resolvedPrimary || placeholderSrc,
      images,
      primaryImageUrlRaw: primaryRaw?.trim() || undefined,
    };
  });
}

/**
 * deleteGalleryImage: POST body `{ imageIdList: string[] }` (image URLs or ids as returned by the API).
 */
export async function postDeleteGalleryImages(imageUrls: string[]): Promise<void> {
  if (imageUrls.length === 0) return;
  await Api.post(QUERY_KEY.deleteGalleryImage, { imageIdList: imageUrls });
}

/** Primary URL to send in imageIdList (raw API value when present, else resolved thumb). */
export function getGalleryCardDeleteImageUrl(
  item: GalleryCardModel,
  placeholderSrc: string,
): string | null {
  const raw = item.primaryImageUrlRaw?.trim();
  if (raw) return raw;
  const display = item.imageUrl?.trim();
  if (display && display !== placeholderSrc) return display;
  return null;
}
